import { useRouteState } from '../routeState'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Check, FolderPlus, HelpCircle, ListTree, Moon, Plus, RefreshCw, Search, Sun } from 'lucide-react'
import { useTasks } from '../TaskProvider'
import { readBrowserValue, writeBrowserValue } from '../browserStorage'
import { guide } from '../helpGuide'
import { localDateKey } from '../taskReminders'
import { matchingTaskCount, taskAncestors, taskTree, visibleTaskBranches, type TaskFilters } from '../taskTree'
import type { ProjectSummary, StoryProject, Theme, WritingTask } from '../types'
import { TaskEditor, type TaskDraft } from './TaskEditor'
import { TaskBranchRow } from './TaskBranchRow'
import { WorkspaceDialog } from './WorkspaceDialog'
import { GuidedTour } from './GuidedTour'
import { TaskOrganizer } from './TaskOrganizer'

export function TasksPage({ project, projects, theme, onThemeChange, onSaveStory, onOpenTarget, onProfile, backLabel }: {
  project: StoryProject | null
  projects: ProjectSummary[]
  theme: Theme
  onThemeChange: (theme: Theme) => void
  onSaveStory: () => Promise<boolean>
  onOpenTarget: (projectId: string, targetId?: string) => Promise<void>
  onProfile: (body: unknown) => Promise<void>
  backLabel: string
}) {
  const store = useTasks()
  const [organize, setOrganize] = useRouteState<boolean>('task-organize', false)
  const [scope, setScope] = useRouteState<TaskFilters['scope']>('task-scope', 'mine')
  const [status, setStatus] = useRouteState<TaskFilters['status']>('task-status', 'open')
  const [projectId, setProjectId] = useRouteState<string>('task-project', '')
  const [search, setSearch] = useState('')
  const [due, setDue] = useRouteState<TaskFilters['due']>('task-due', 'all')
  const [draft, setDraftState] = useState<TaskDraft | null>(null)
  const [editRoute, setEditRoute] = useRouteState<string | null>('task-edit', null)
  const draftKey = (value: TaskDraft | null) => value ? value.task ? 'edit:' + value.task.id : (value.kind || 'task') + ':' + (value.parent?.id || '') : null
  const setDraft = (value: TaskDraft | null) => { setDraftState(value); setEditRoute(draftKey(value)) }
  useEffect(() => {
    // Opening a local draft and committing Router's location can render
    // separately. An effect from the previous URL must not clear that draft.
    if (editRoute !== new URLSearchParams(window.location.search).get('task-edit')) return
    if (editRoute === draftKey(draft) || !store.loaded) return
    if (!editRoute) { setDraftState(null); return }
    const split = editRoute.indexOf(':'), kind = editRoute.slice(0, split), id = editRoute.slice(split + 1)
    const task = id ? store.tasks.find(task => task.id === id) : undefined
    if (id && !task) { setError('This task is private, deleted, or unavailable to your account.'); setDraftState(null); return }
    if (kind === 'edit' && task) setDraftState({ task, scope: task.scope, serial: Date.now() })
    else if (kind === 'task' || kind === 'group') setDraftState({ kind, parent: task, scope: task?.scope || (scope === 'personal' ? 'personal' : 'team'), serial: Date.now() })
  }, [editRoute, store.loaded, store.tasks])
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [focusId, setFocusId] = useState('')
  const [tourOpen, setTourOpen] = useRouteState<boolean>('task-tour', false)
  const [tourIndex, setTourIndex] = useRouteState<number>('task-tour-step', 0)
  const tour = tourOpen ? Math.max(0, Math.min(guide.tasks.steps.length - 1, Number.isInteger(tourIndex) ? tourIndex : 0)) : null
  const setTour = (index: number | null) => { setTourOpen(index !== null); setTourIndex(index ?? 0) }
  const [savingTour, setSavingTour] = useState(false)
  const content = useRef<HTMLElement>(null)
  const heading = useRef<HTMLHeadingElement>(null)
  const collapseKey = 'bramble:task-branches:' + store.user.id
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(readBrowserValue(collapseKey) || '[]')) } catch { return new Set() }
  })
  const today = localDateKey()
  const tree = useMemo(() => taskTree(store.tasks, { userId: store.user.id, scope, status, projectId, search, due, today }),
    [store.tasks, store.user.id, scope, status, projectId, search, due, today])
  const count = matchingTaskCount(tree)
  const requested = useRef('')
  useEffect(() => { writeBrowserValue(collapseKey, JSON.stringify([...collapsed])) }, [collapsed, collapseKey])
  useLayoutEffect(() => { heading.current?.focus({ preventScroll: true }) }, [])
  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (!await onSaveStory()) {
        if (!cancelled) setError('Your writing is still in the local draft. Save it successfully to update linked task names and parents.')
        return
      }
      if (!cancelled) await store.refresh()
    })()
    return () => { cancelled = true }
  }, [store.page?.serial])
  const reveal = (task: WritingTask) => {
    const parents = taskAncestors([...store.tasks.filter(t => t.id !== task.id), task], task.id)
    setCollapsed(old => new Set([...old].filter(id => !parents.some(p => p.id === id) && id !== task.id)))
    setFocusId(task.id)
  }
  useEffect(() => {
    if (!store.page || !store.loaded || requested.current === store.page.serial) return
    if (store.page.taskId) {
      const task = store.tasks.find(t => t.id === store.page!.taskId)
      if (!task) { setError('This task is private, deleted, or unavailable to your account.'); requested.current = store.page.serial; return }
      setScope(task.scope)
      const descendants = store.tasks.filter(t => t.kind !== 'group' && taskAncestors(store.tasks, t.id).some(p => p.id === task.id))
      setStatus(task.archived || (task.kind === 'group' && descendants.length > 0 && descendants.every(t => t.archived)) ? 'archive' : 'all')
      setSearch(''); setDue('all'); setProjectId('')
      reveal(task)
    }
    requested.current = store.page.serial
  }, [store.page, store.loaded, store.tasks])
  useEffect(() => {
    if (!focusId || draft) return
    const frame = requestAnimationFrame(() => {
      const element = document.getElementById('task-' + focusId)
      element?.scrollIntoView({ block: 'center' })
      element?.focus({ preventScroll: true })
      if (element) setFocusId('')
    })
    return () => cancelAnimationFrame(frame)
  }, [focusId, draft, tree])
  const start = (kind: 'task' | 'group', parent?: WritingTask) => {
    setError(''); setNotice(''); setFocusId('')
    setDraft({ kind, parent, scope: parent?.scope || (scope === 'personal' ? 'personal' : 'team'), serial: Date.now() })
  }
  const edit = (task: WritingTask) => {
    setError(''); setNotice(''); setFocusId('')
    setDraft({ task, scope: task.scope, serial: Date.now() })
  }
  const currentDraft = draft
  const save = async (body: Partial<WritingTask>) => {
    setError('')
    try {
      if (body.projectId === project?.id && !await onSaveStory()) throw new Error('Save this story successfully before linking a task to it.')
      const task = await store.save(body, currentDraft?.task)
      setScope(task.kind === 'group' ? task.scope : task.assigneeId === store.user.id && scope === 'mine' ? 'mine' : task.scope)
      setStatus(task.archived ? 'archive' : task.status === 'done' ? 'all' : 'open')
      setSearch(''); setDue('all'); setProjectId(''); setDraft(null)
      reveal(task)
      setNotice(task.kind === 'group' ? 'Group created. Add its first task with +.' : 'Task saved.')
    } catch (e) { setError(e instanceof Error ? e.message : 'The task could not be saved.') }
  }
  const change = async (task: WritingTask, body: Partial<WritingTask>) => {
    setError(''); setNotice(''); setFocusId('')
    try { await store.save(body, task) } catch (e) { setError(e instanceof Error ? e.message : 'This task could not be changed.') }
  }
  const openWriting = async (task: WritingTask) => {
    setError('')
    try { await onOpenTarget(task.projectId, task.targetId); store.setWritingScope(task.scope); store.closePage() }
    catch (e) { setError(e instanceof Error ? e.message : 'The linked writing could not be opened.') }
  }
  const finishTour = async (status: 'completed' | 'dismissed') => {
    setSavingTour(true); setError('')
    try { await onProfile({ tour: { id: 'tasks', status } }); setTour(null); setDraft(null); return true }
    catch (e) { setError(e instanceof Error ? e.message : 'Tour progress could not be saved.'); return false }
    finally { setSavingTour(false) }
  }
  const changeScope = (value: typeof scope) => { setScope(value); setDue('all'); setFocusId('') }
  return <div className="tasks-page">
    <header className="tasks-page-header">
      <button type="button" className="text-button tasks-back" disabled={savingTour} onClick={() => {
        if (tour !== null) void finishTour('dismissed').then(saved => { if (saved) store.closePage() })
        else store.closePage()
      }}><ArrowLeft size={16} />{backLabel}</button>
      <div className="tasks-page-tools"><span data-room-tools />
        <button type="button" className="icon-button" aria-label={'Switch to ' + (theme === 'light' ? 'dark' : 'light') + ' theme'}
          onClick={() => onThemeChange(theme === 'light' ? 'dark' : 'light')}>{theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}</button>
      </div>
    </header>
    <main ref={content} className="tasks-page-content" data-tour="tasks">
      <div className="tasks-page-heading">
        <h1 tabIndex={-1} ref={heading}>Tasks <span>{store.loaded ? count : '…'}</span></h1>
        <div className="tasks-page-actions">
          <button type="button" className="button" data-tour="organize-tasks" onClick={() => setOrganize(true)}><ListTree size={15} />Organize</button>
          <button type="button" className="button" data-tour="new-group" onClick={() => start('group')}><FolderPlus size={15} />New group</button>
          <button type="button" className="button button--primary" data-tour="new-task" onClick={() => start('task')}><Plus size={15} />New task</button>
        </div>
      </div>
      <div className="tasks-page-filters">
        <div className="segmented-control" aria-label="Task visibility">
          <button type="button" aria-pressed={scope === 'mine'} onClick={() => changeScope('mine')}>My tasks</button>
          <button type="button" aria-pressed={scope === 'team'} onClick={() => changeScope('team')}>Team</button>
          <button type="button" aria-pressed={scope === 'personal'} onClick={() => changeScope('personal')}>Personal</button>
        </div>
        <label className="task-search"><Search size={15} /><input aria-label="Search tasks" placeholder="Find a task or group…"
          value={search} onChange={e => { setSearch(e.target.value); setFocusId(''); if (e.target.value.trim()) setCollapsed(new Set()) }} /></label>
        <select aria-label="Filter tasks by story" value={projectId} onChange={e => { setProjectId(e.target.value); setFocusId('') }}>
          <option value="">All stories</option>{projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <select aria-label="Task status filter" value={status} onChange={e => { setStatus(e.target.value as typeof status); setDue('all'); setFocusId('') }}>
          <option value="open">Open</option><option value="all">All statuses</option><option value="archive">Archived</option>
        </select>
      </div>
      <div className="tasks-list-toolbar">
        <div className="task-due-filters" aria-label="Task due filters">
          {(['all', 'today', 'overdue'] as const).map(value => <button type="button" key={value} aria-pressed={due === value}
            onClick={() => { setDue(value); setStatus('open'); setFocusId('') }}>{value === 'all' ? 'Any date' : value === 'today' ? 'Today' : 'Overdue'}</button>)}
        </div>
        <div className="task-tree-tools">
          <button type="button" className="text-button" onClick={() => { setCollapsed(new Set(store.tasks.filter(t => store.tasks.some(c => c.parentId === t.id)).map(t => t.id))); setFocusId('') }}>Collapse all</button>
          <button type="button" className="text-button" onClick={() => setCollapsed(new Set())}>Expand all</button>
          <button type="button" className="icon-button" aria-label="Refresh tasks" disabled={store.loading || store.busy} onClick={() => void store.refresh()}><RefreshCw size={15} /></button>
          <button type="button" className="icon-button" aria-label="Tasks guide and tour" onClick={() => { setError(''); setTour(0) }}><HelpCircle size={16} /></button>
        </div>
      </div>
      {!draft && (error || store.error) && <p role="alert" className="task-page-error">{error || store.error}</p>}
      {notice && <p role="status" className="task-page-notice"><Check size={14} />{notice}</p>}
      {!store.loaded && store.loading ? <p role="status">Opening tasks…</p> : <ul className="task-tree" aria-label="Task list">
        {visibleTaskBranches(tree, collapsed).map(({ branch, depth }) => <TaskBranchRow key={branch.task.id} branch={branch} depth={depth} renderChildren={false} collapsed={collapsed}
          toggle={id => { setFocusId(''); setCollapsed(old => { const value = new Set(old); value.has(id) ? value.delete(id) : value.add(id); return value }) }}
          busy={store.busy} today={today} members={store.members} projects={projects} onEdit={edit}
          onAdd={parent => start('task', parent)} onChange={(task, body) => void change(task, body)} onOpen={task => void openWriting(task)} />)}
      </ul>}
      {store.loaded && !tree.length && <div className="tasks-empty">
        <h2>{search || projectId || due !== 'all' ? 'No matching tasks' : status === 'archive' ? 'No archived tasks' : 'Nothing waiting here'}</h2>
        <p>{search || projectId || due !== 'all' ? 'Try another search or adjust the filters.' : 'Add a next step here, or choose Add to tasks beside your writing.'}</p>
      </div>}
    </main>
    {draft && <WorkspaceDialog className="task-edit-dialog" title={draft.task ? draft.task.kind === 'group' ? 'Edit group' : 'Edit task' : draft.kind === 'group' ? 'New group' : draft.parent ? 'New subtask' : 'New task'}
      eyebrow={draft.parent ? 'IN ' + draft.parent.title : 'KEEP THE NEXT STEP CLEAR'} onClose={() => { setDraft(null); setError('') }}>
      <div className="task-editor-scroll"><TaskEditor key={draft.serial} draft={draft} project={project} projects={projects}
        error={error} onSave={save} onCancel={() => { setDraft(null); setError('') }} /></div>
    </WorkspaceDialog>}
    {organize && <TaskOrganizer initialScope={scope === 'personal' ? 'personal' : 'team'} onClose={() => setOrganize(false)} />}
    {tour !== null && <GuidedTour title={guide.tasks.title} steps={guide.tasks.steps} index={tour}
      busy={savingTour} error={error} onStep={index => { setError(''); setTour(index) }}
      onFinish={status => void finishTour(status)} onLeave={() => { setTour(null); setError('') }} />}
  </div>
}
